import Head from "next/head";
import ErrorPage from "next/error";
import Layout from "@/components/layout";
import BlogContainer from "@/components/blog/blog-container";
import MorePosts from "@/components/blog/more-posts";
import Header from "@/components/blog/header";
import SectionSeparator from "@/components/blog/section-separator";
import PostTitle from "@/components/blog/post-title";
import CoverImage from "@/components/blog/cover-image";
import PostBody from "@/components/blog/post-body";
import PostMeta from "@/components/blog/post-meta";
import SocialShareButtons from "@/components/blog/social-share-buttons";
import { getSegmentProps } from "@/components/segment-generator";
import { urlForImage, usePreviewSubscription } from "@/lib/sanity";
import classNames from "classnames";
import { postQuery } from "@/lib/queries";
import ratingSchema from "@/components/rating-schema-jsonld";

export default function BlogPost({ blogMetaContent, data = {}, preview }) {
  const slug = data?.post?.slug;
  const {
    data: { post, morePosts },
  } = usePreviewSubscription(postQuery, {
    params: { slug },
    initialData: data,
    enabled: preview && slug,
  });
  if (!data.post) {
    return <ErrorPage statusCode={404} />;
  }

  const headerSegment = blogMetaContent.segments?.find((segment) =>
    segment.name.toLowerCase().includes("header")
  );

  return (
    <Layout preview={preview}>
      <Head>
        <title>{post.metaTitle || post.title}</title>
        <meta
          name="description"
          content={post.metaDescription || post.excerpt}
        />
        {post.coverImage && (
          <meta
            key="ogImage"
            property="og:image"
            content={urlForImage(post.coverImage)
              .width(1200)
              .height(627)
              .fit("crop")
              .url()}
          />
        )}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.metaTitle || post.title} />
        <meta
          property="og:description"
          content={post.metaDescription || post.excerpt}
        />
        <meta
          property="og:url"
          content={`${process.env.NEXT_PUBLIC_HOST}/${post.slug}`}
        />
        <meta
          property="og:site_name"
          content="Blog | JustShip International Shipping Singapore | Learn about International Shipping"
        />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:modified_time" content={post.date} />
        {ratingSchema()}
      </Head>

      <Header {...getSegmentProps(headerSegment)} />
      <BlogContainer>
        <article className="relative">
          <div className="w-full lg:w-3/4 text-center lg:text-left">
            <PostTitle>{post.title}</PostTitle>
          </div>
          <div className="hidden lg:block md:sticky top-1/3 z-10">
            <div
              className={classNames(
                "absolute -left-10 xl:-left-20 2xl:-left-28 my-1",
                "flex flex-col gap-6 text-dark-purple items-center"
              )}
            >
              <SocialShareButtons post={post} />
            </div>
          </div>
          <div className="flex flex-wrap-reverse justify-center lg:flex-nowrap">
            <div className="lg:w-2/3">
              <div className="mb-8 md:mb-16 sm:mx-0">
                <CoverImage title={post.title} image={post.coverImage} />
              </div>
              <PostBody content={post.body} />
              <div className="my-8 flex gap-x-6">
                <span>Share:</span>
                <div className="flex gap-x-6 text-dark-purple">
                  <SocialShareButtons post={post} />
                </div>
              </div>
            </div>
            <div className="lg:w-1/3">
              <PostMeta post={post} />
            </div>
          </div>
        </article>
        <SectionSeparator />
        {morePosts.length > 0 && (
          <MorePosts title="Related posts" posts={morePosts} />
        )}
      </BlogContainer>
    </Layout>
  );
}
